import { isValidJson, safeJsonParse } from './jsonUtils'

type DiffOp = { type: 'equal' | 'add' | 'del'; line: string }
type StateChange = {
  stateId: string
  changes: Array<{
    field: string
    oldValue: unknown
    newValue: unknown
    type: 'modified' | 'added' | 'removed'
  }>
}

interface StateMachineDefinition {
  states?: Array<Record<string, unknown>>
}

type ChangeRecord = {
  field: string
  oldValue: unknown
  newValue: unknown
  type: 'modified' | 'added' | 'removed'
}

/**
 * Compare two arrays and return differences
 */
const compareArrays = (arr1: unknown[], arr2: unknown[], path: string): ChangeRecord[] => {
  const changes: ChangeRecord[] = []
  const maxLength = Math.max(arr1.length, arr2.length)

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < maxLength; i++) {
    const itemPath = `${path}[${i}]`

    if (i >= arr1.length) {
      changes.push({
        field: itemPath,
        oldValue: undefined,
        newValue: arr2[i],
        type: 'added'
      })
    } else if (i >= arr2.length) {
      changes.push({
        field: itemPath,
        oldValue: arr1[i],
        newValue: undefined,
        type: 'removed'
      })
    } else {
      const itemChanges = deepCompare(arr1[i], arr2[i], itemPath)

      if (itemChanges.length > 0) {
        changes.push(...itemChanges)
      }
    }
  }

  return changes
}

/**
 * Compare two objects and return differences
 */
const compareObjects = (obj1: Record<string, unknown>, obj2: Record<string, unknown>, path: string): ChangeRecord[] => {
  const changes: ChangeRecord[] = []
  const obj1Keys = Object.keys(obj1)
  const obj2Keys = Object.keys(obj2)
  const allKeys = new Set([...obj1Keys, ...obj2Keys])

  for (const key of allKeys) {
    const fieldPath = path ? `${path}.${key}` : key
    const inObj1 = key in obj1
    const inObj2 = key in obj2

    if (inObj1 && inObj2) {
      const fieldChanges = deepCompare(obj1[key], obj2[key], fieldPath)

      if (fieldChanges.length > 0) {
        changes.push(...fieldChanges)
      }
    } else if (inObj1) {
      changes.push({
        field: fieldPath,
        oldValue: obj1[key],
        newValue: undefined,
        type: 'removed'
      })
    } else {
      changes.push({
        field: fieldPath,
        oldValue: undefined,
        newValue: obj2[key],
        type: 'added'
      })
    }
  }

  return changes
}

/**
 * Deep comparison of objects to find differences
 */
const deepCompare = (obj1: unknown, obj2: unknown, path = ''): ChangeRecord[] => {
  if (obj1 === obj2) return []

  if (typeof obj1 !== typeof obj2) {
    return [{ field: path, oldValue: obj1, newValue: obj2, type: 'modified' }]
  }

  if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
    if (obj1 !== obj2) {
      return [{ field: path, oldValue: obj1, newValue: obj2, type: 'modified' }]
    }

    return []
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    return compareArrays(obj1, obj2, path)
  }

  return compareObjects(obj1 as Record<string, unknown>, obj2 as Record<string, unknown>, path)
}

/**
 * Find changes in state machine definitions
 */
const findStateChanges = (originalJson: string, editedJson: string): StateChange[] => {
  try {
    const original = safeJsonParse<StateMachineDefinition>(originalJson)
    const edited = safeJsonParse<StateMachineDefinition>(editedJson)

    if (!original?.states || !edited?.states) {
      return []
    }

    const changes: StateChange[] = []

    // Create maps for easier lookup
    const originalStates = new Map(original.states.map((state: Record<string, unknown>) => [state.id as string, state]))
    const editedStates = new Map(edited.states.map((state: Record<string, unknown>) => [state.id as string, state]))

    // Check for modified states
    for (const [stateId, originalState] of originalStates) {
      const editedState = editedStates.get(stateId)

      if (editedState) {
        // State exists in both, check for changes
        const stateChanges = deepCompare(originalState, editedState)

        if (stateChanges.length > 0) {
          changes.push({
            stateId,
            changes: stateChanges
          })
        }
      } else {
        // State was removed
        changes.push({
          stateId,
          changes: [
            {
              field: 'state',
              oldValue: originalState,
              newValue: undefined,
              type: 'removed'
            }
          ]
        })
      }
    }

    // Check for added states
    for (const [stateId, editedState] of editedStates) {
      if (!originalStates.has(stateId)) {
        changes.push({
          stateId,
          changes: [
            {
              field: 'state',
              oldValue: undefined,
              newValue: editedState,
              type: 'added'
            }
          ]
        })
      }
    }

    return changes
  } catch {
    return []
  }
}

/**
 * Convert state changes to diff operations
 */
const stateChangesToDiffOps = (stateChanges: StateChange[]): DiffOp[] => {
  if (stateChanges.length === 0) {
    return [{ type: 'equal', line: 'No changes detected' }]
  }

  const ops: DiffOp[] = []

  for (const stateChange of stateChanges) {
    // Add state header
    ops.push({ type: 'equal', line: `State: ${stateChange.stateId}` }, { type: 'equal', line: '─'.repeat(50) })

    for (const change of stateChange.changes) {
      if (change.type === 'modified') {
        ops.push(
          { type: 'del', line: `  ${change.field}: ${JSON.stringify(change.oldValue)}` },
          { type: 'add', line: `  ${change.field}: ${JSON.stringify(change.newValue)}` }
        )
      } else if (change.type === 'added') {
        ops.push({ type: 'add', line: `  ${change.field}: ${JSON.stringify(change.newValue)}` })
      } else if (change.type === 'removed') {
        ops.push({ type: 'del', line: `  ${change.field}: ${JSON.stringify(change.oldValue)}` })
      }
    }

    ops.push({ type: 'equal', line: '' }) // Empty line between states
  }

  return ops
}

export const getDiffLines = (originalText: string, editedText: string): DiffOp[] => {
  // First check if JSON is valid
  if (!isValidJson(originalText) || !isValidJson(editedText)) {
    return [{ type: 'equal', line: 'Invalid JSON - cannot compare' }]
  }

  // Find state changes
  const stateChanges = findStateChanges(originalText, editedText)

  // Convert to diff operations
  return stateChangesToDiffOps(stateChanges)
}
